import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { addDays, format } from 'date-fns';
import {
  GetClinicalConstraintTemplatesForEpisodeQuery,
  GetClinicalConstraintTemplatesForEpisodeQueryVariables,
} from '~/graphql/__generated__/graphql';
import { GET_CLINICAL_CONSTRAINT_TEMPLATES_FOR_EPISODE_QUERY } from '~/queries/clinicalConstraintTemplate';
import { useOrgConfig } from '~/providers/OrgConfigProvider';
import { usePredictContext } from '~/hooks/predict/usePredictContext';
import { CapacityDay, CapacityStatus, PredictPatient } from '~/types/predict';
import { hashUnit, pickDefaultTemplate } from '~/utils/clinicalTemplates';

type CapacityTemplates = GetClinicalConstraintTemplatesForEpisodeQuery['clinicalConstraintTemplate'];
type CapacityTemplate = CapacityTemplates[number];

interface CapacityStatusMeta {
  label: string;
  color: string;
  Icon: typeof CheckCircleIcon;
}

// Visual treatment per outcome. AVAILABLE -> success, LIMITED -> warning,
// UNAVAILABLE -> do not disturb.
export const CAPACITY_STATUS_META: Record<CapacityStatus, CapacityStatusMeta> = {
  [CapacityStatus.AVAILABLE]: { label: 'Capacity available', color: 'success.main', Icon: CheckCircleIcon },
  [CapacityStatus.LIMITED]: { label: 'Limited capacity', color: 'warning.main', Icon: WarningAmberIcon },
  [CapacityStatus.UNAVAILABLE]: { label: 'No capacity', color: 'error.main', Icon: DoNotDisturbOnIcon },
};

// Short per-cell label for an availability status.
const CELL_LABEL: Record<CapacityStatus, string> = {
  [CapacityStatus.AVAILABLE]: 'Available',
  [CapacityStatus.LIMITED]: 'Limited',
  [CapacityStatus.UNAVAILABLE]: 'Unavailable',
};

// Number of subsequent days to show. Bounded by the template's episode length.
const MAX_DAYS = 4;

const MINUTES_PER_DAY = 24 * 60;

// Parse a wall-clock time string ("09:00" or "09:00:00") to minutes since midnight.
const parseWallTimeMinutes = (value: string): number | null => {
  const [hRaw, mRaw] = value.split(':');
  const hours = Number(hRaw);
  const minutes = Number(mRaw ?? 0);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  return hours * 60 + minutes;
};

// Format minutes-since-midnight as "9:00 AM". Times past midnight wrap and are
// annotated with the day offset (e.g. "9:00 AM (+1d)").
const formatClockMinutes = (totalMinutes: number): string => {
  const dayOffset = Math.floor(totalMinutes / MINUTES_PER_DAY);
  const minuteOfDay = ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(minuteOfDay / 60);
  const minutes = minuteOfDay % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 === 0 ? 12 : hours % 12;
  const suffix = dayOffset > 0 ? ` (+${dayOffset}d)` : '';
  return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}${suffix}`;
};

// Build a label from an optional start/end window.
const formatRangeLabel = (
  startMin: number | null,
  endMin: number | null,
  durationMin: number,
): string => {
  if (startMin != null && endMin != null) {
    return `${formatClockMinutes(startMin)} – ${formatClockMinutes(endMin)}`;
  }
  if (startMin != null) {
    return `From ${formatClockMinutes(startMin)}`;
  }
  if (endMin != null) {
    return `By ${formatClockMinutes(endMin)}`;
  }
  if (durationMin) {
    return `~${durationMin} min visit`;
  }
  return 'Flexible timing';
};

interface VisitTiming {
  label: string;
  // Start minute-of-(first-)day, used to bucket the visit into a time slot for
  // availability lookup. May exceed 24h for relative chains; the bucket wraps.
  startMinutes: number | null;
}

// Compute the time range + start minute for each template visit requirement.
// Absolute requirements use their earliest/latest window. "Relative to last
// visit" requirements shift the prior visit's window: the start moves by the
// min offset and the end by the max offset (both minutes), so the window keeps
// its width instead of collapsing to a point when min == max.
const computeVisitTimings = (requirements: CapacityTemplate['requirements']): VisitTiming[] => {
  let prevStartMinutes: number | null = null;
  let prevEndMinutes: number | null = null;

  return requirements.map((req) => {
    let startMin: number | null = null;
    let endMin: number | null = null;

    const hasAbsolute = req.earliestStartTime != null || req.latestStartTime != null;
    const hasRelative = req.minTimeFromLastVisit != null || req.maxTimeFromLastVisit != null;

    if (hasAbsolute) {
      startMin = req.earliestStartTime ? parseWallTimeMinutes(req.earliestStartTime) : null;
      endMin = req.latestStartTime ? parseWallTimeMinutes(req.latestStartTime) : null;
    } else if (hasRelative && (prevStartMinutes != null || prevEndMinutes != null)) {
      const startOffset = req.minTimeFromLastVisit ?? req.maxTimeFromLastVisit;
      const endOffset = req.maxTimeFromLastVisit ?? req.minTimeFromLastVisit;
      const anchorStart = prevStartMinutes ?? prevEndMinutes;
      const anchorEnd = prevEndMinutes ?? prevStartMinutes;
      startMin = anchorStart != null && startOffset != null ? anchorStart + startOffset : null;
      endMin = anchorEnd != null && endOffset != null ? anchorEnd + endOffset : null;
    }

    // Anchor the next relative visit on this visit's window.
    if (startMin != null || endMin != null) {
      prevStartMinutes = startMin ?? endMin;
      prevEndMinutes = endMin ?? startMin;
    }

    return { label: formatRangeLabel(startMin, endMin, req.duration), startMinutes: startMin };
  });
};

// This program runs like a pipeline: there are no planned admits several days
// out, so contention concentrates on the near-term days and frees up further
// into the stay (a residual amount remains for occasional later-week staffing
// issues). `relief` grows with the day offset and progressively eases a slot's
// contention.
const RELIEF_PER_DAY = 0.35;
const RELIEF_CAP = 0.85;

// Deterministic availability for a given slot. Keyed only on (patient, day,
// time bucket) so the same future day + time resolves to the same status
// regardless of which template requested it. The patient's pool sets the base
// lean; later days are then relieved toward available.
const slotStatus = (
  pool: CapacityStatus[],
  patientId: string,
  dayOffset: number,
  bucket: number,
): CapacityStatus => {
  const key = `${patientId}:${dayOffset}:${bucket}`;
  const base = pool[Math.floor(hashUnit(key) * pool.length)];
  if (base === CapacityStatus.AVAILABLE) {
    return base;
  }

  const relief = Math.min(RELIEF_CAP, (dayOffset - 1) * RELIEF_PER_DAY);
  const roll = hashUnit(`${key}:relief`);
  if (base === CapacityStatus.UNAVAILABLE) {
    if (roll < relief * 0.6) {
      return CapacityStatus.AVAILABLE;
    }
    if (roll < relief) {
      return CapacityStatus.LIMITED;
    }
    return CapacityStatus.UNAVAILABLE;
  }
  // LIMITED eases straight to available as the stay progresses.
  return roll < relief ? CapacityStatus.AVAILABLE : CapacityStatus.LIMITED;
};

// Bucket a visit into a stable time slot. Timed visits bucket by hour-of-day;
// untimed visits bucket by visit type so the same type on the same day is still
// consistent (offset to avoid colliding with hour buckets).
const slotBucket = (startMinutes: number | null, visitTypeId: number): number => {
  return startMinutes != null ? Math.floor((startMinutes % MINUTES_PER_DAY) / 60) : 1000 + visitTypeId;
};

// Minutes-since-midnight of an admit slot's start time, parsed from its label
// (e.g. "8:00 AM – 9:00 AM" -> 480). Returns null if it can't be parsed.
const parseSlotStartMinutes = (slot: string): number | null => {
  const match = slot.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) {
    return null;
  }
  const hours = Number(match[1]) % 12 + (/pm/i.test(match[3]) ? 12 : 0);
  return hours * 60 + Number(match[2]);
};

// Relative label for a subsequent day. Offset is days from today (>= 1).
const formatDayLabel = (offset: number): string => {
  if (offset === 1) {
    return 'Tomorrow';
  }
  return format(addDays(new Date(), offset), 'EEE, MMM d');
};

// Derive the overall status from the admit slot count and the day grid statuses,
// per the capacity rules:
// - RED ("No capacity"): genuinely no capacity — no admit slots at all, or the
//   ongoing availability is dominated by unavailable slots.
// - GREEN: multiple admit slots AND a mostly-green grid with only a couple
//   yellows and no reds.
// - YELLOW: anything in between — there's some contention but it's workable
//   (e.g. a single admit slot, or a mix of limited/available with few reds).
const deriveOverallStatus = (admitSlotCount: number, cells: CapacityStatus[]): CapacityStatus => {
  const green = cells.filter((c) => c === CapacityStatus.AVAILABLE).length;
  const yellow = cells.filter((c) => c === CapacityStatus.LIMITED).length;
  const red = cells.filter((c) => c === CapacityStatus.UNAVAILABLE).length;
  const total = cells.length;

  const mostlyGreen = total === 0 ? true : green >= Math.ceil(total / 2);
  const mostlyRed = total > 0 && red >= Math.ceil(total / 2);
  // "A couple" scales with grid size but is at least 2.
  const yellowAllowance = Math.max(2, Math.round(total * 0.25));

  // No capacity only when there's nothing to admit into, or unavailability
  // genuinely dominates the ongoing visits.
  if (admitSlotCount === 0 || mostlyRed) {
    return CapacityStatus.UNAVAILABLE;
  }
  // Fully available: multiple admit slots, no reds, mostly green.
  if (admitSlotCount >= 2 && red === 0 && yellow <= yellowAllowance && mostlyGreen) {
    return CapacityStatus.AVAILABLE;
  }
  // Otherwise there's contention but capacity is workable.
  return CapacityStatus.LIMITED;
};

interface CapacityTabProps {
  patient: PredictPatient;
}

/**
 * Capacity Tab (demo)
 *
 * Lets the user pick a clinical template (the same templates used when creating
 * an episode) and check capacity for it. Pressing the button shows a spinner for
 * a random 3-5 seconds, then a result with two sections:
 *   1. Admit visit slots available today.
 *   2. Upcoming visits (derived from the template) per subsequent day, each with
 *      an availability status.
 * The overall status (success / warning / do-not-disturb) is derived from those.
 * Admit slots and per-cell availability come from the predefined demo data; the
 * visit list, time ranges, and day count are derived from the template. The
 * result is persisted on the patient so revisiting the tab shows it until the
 * user checks again.
 */
const CapacityTab: React.FC<CapacityTabProps> = ({ patient }) => {
  const { updatePatient } = usePredictContext();
  const orgConfig = useOrgConfig();

  // Templates come from the same live query that drives episode creation. The
  // predict demo isn't scoped to a single service line, so use the first one
  // available for the org.
  const serviceLineId = Object.values(orgConfig.serviceLines).find(Boolean)?.id;

  const { data } = useQuery<
    GetClinicalConstraintTemplatesForEpisodeQuery,
    GetClinicalConstraintTemplatesForEpisodeQueryVariables
  >(GET_CLINICAL_CONSTRAINT_TEMPLATES_FOR_EPISODE_QUERY, {
    variables: { serviceLineId: serviceLineId ?? 0 },
    skip: serviceLineId === undefined,
    fetchPolicy: 'cache-and-network',
  });

  const templates: CapacityTemplates = data?.clinicalConstraintTemplate ?? [];

  // We're loading while the org config is still resolving the service line, or
  // while the templates query is in flight and has no data yet. (The query's own
  // `loading` reads false while it's skipped waiting on the service line, so we
  // can't rely on it alone.)
  const isLoadingTemplates =
    orgConfig.loading || (serviceLineId !== undefined && data === undefined);

  // Default the dropdown to whatever was last checked, if anything.
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>(patient.capacityCheck?.templateId ?? 0);
  const [isChecking, setIsChecking] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A default template is auto-selected per patient (demo: as if chosen from
  // recent chart activity). Deterministic so it's stable across renders, and
  // never "Short Stay" — but that one stays selectable in the dropdown.
  const defaultTemplateId = useMemo(
    () => pickDefaultTemplate(templates, patient.id)?.id ?? 0,
    [templates, patient.id],
  );

  // Auto-select the default once templates load, unless the user already has a
  // selection or a prior check to restore.
  useEffect(() => {
    if (defaultTemplateId !== 0 && selectedTemplateId === 0 && !patient.capacityCheck) {
      setSelectedTemplateId(defaultTemplateId);
    }
  }, [defaultTemplateId]);

  // Clear any pending timer if the component unmounts mid-check.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Run a capacity check for the given template. `admitDayShift` is 0 to admit
  // today (ongoing visits start tomorrow) or 1 to admit tomorrow (ongoing visits
  // shift out an extra day).
  const runCheck = (template: CapacityTemplate, admitDayShift: 0 | 1) => {
    setIsChecking(true);

    // The patient's capacity calendar drives availability. It's per-patient (not
    // per-template) so results stay consistent across templates.
    const calendar = patient.capacityCalendar ?? { admitSlots: [], statusPool: [CapacityStatus.AVAILABLE] };
    const pool = calendar.statusPool.length ? calendar.statusPool : [CapacityStatus.AVAILABLE];

    // Visits derived from the template's requirements. Time ranges chain through
    // the requirements so "relative to last visit" entries resolve to a clock
    // window rather than falling back to a duration.
    const timings = computeVisitTimings(template.requirements);
    const visits = template.requirements.map((req, i) => ({
      visitTypeName: orgConfig.visitTypes[req.visitTypeId]?.name ?? `Visit type ${req.visitTypeId}`,
      timeRange: timings[i].label,
      bucket: slotBucket(timings[i].startMinutes, req.visitTypeId),
    }));

    // Number of subsequent days derived from the template's episode length.
    const numDays = Math.min(MAX_DAYS, Math.max(1, (template.durationDays || MAX_DAYS) - 1));

    // Fill the per-visit-per-day grid. Each cell's status is looked up by
    // (patient, day, time slot) so the same slot resolves identically across
    // templates. Admitting tomorrow shifts every ongoing day out by one.
    const allCells: CapacityStatus[] = [];
    const days: CapacityDay[] = Array.from({ length: numDays }, (_, dayIdx) => {
      const dayOffset = dayIdx + 1 + admitDayShift;
      return {
        label: formatDayLabel(dayOffset),
        visits: visits.map((visit) => {
          const status = slotStatus(pool, patient.id, dayOffset, visit.bucket);
          allCells.push(status);
          return { visitTypeName: visit.visitTypeName, timeRange: visit.timeRange, status };
        }),
      };
    });

    let admitSlots: string[];
    if (admitDayShift === 0) {
      // Today: only slots still later than now.
      const now = new Date();
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      admitSlots = calendar.admitSlots.filter((slot) => {
        const startMinutes = parseSlotStartMinutes(slot);
        return startMinutes === null || startMinutes > nowMinutes;
      });
    } else {
      // Tomorrow: all of the day's slots are still open.
      admitSlots = calendar.admitSlots;
    }
    const admitSlotsDay: 'today' | 'tomorrow' = admitDayShift === 1 ? 'tomorrow' : 'today';

    const overallStatus = deriveOverallStatus(admitSlots.length, allCells);

    // Spinner for a random 2-4 seconds, then reveal the result.
    const delayMs = 2000 + Math.random() * 2000;
    timeoutRef.current = setTimeout(() => {
      updatePatient(patient.id, {
        capacityCheck: {
          templateId: template.id,
          templateName: template.name,
          checkedAt: new Date().toISOString(),
          overallStatus,
          admitSlots,
          admitSlotsDay,
          days,
        },
      });
      setIsChecking(false);
      timeoutRef.current = null;
    }, delayMs);
  };

  const handleCheckCapacity = () => {
    const template = templates.find((t) => t.id === selectedTemplateId);
    if (template) {
      runCheck(template, 0);
    }
  };

  // Explicit "no slots today → check tomorrow" action.
  const handleCheckTomorrow = () => {
    const template = templates.find((t) => t.id === (patient.capacityCheck?.templateId ?? selectedTemplateId));
    if (template) {
      runCheck(template, 1);
    }
  };

  const result = patient.capacityCheck;
  const overallMeta = result ? CAPACITY_STATUS_META[result.overallStatus] : null;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a clinical template and check downstream capacity for this patient.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <FormControl size="small" sx={{ minWidth: 320 }}>
            <Select
              value={templates.some((t) => t.id === selectedTemplateId) ? selectedTemplateId : 0}
              onChange={(e) => {
                const templateId = Number(e.target.value);
                if (!Number.isFinite(templateId)) {
                  return;
                }
                setSelectedTemplateId(templateId);
              }}
              disabled={isChecking || isLoadingTemplates}
              displayEmpty
              startAdornment={
                isLoadingTemplates ? <CircularProgress size={16} sx={{ mr: 1 }} /> : undefined
              }
              renderValue={(selected) => {
                if (isLoadingTemplates) {
                  return (
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      Loading templates…
                    </Box>
                  );
                }
                if (selected === 0) {
                  return (
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      Select clinical template
                    </Box>
                  );
                }
                const selectedTemplate = templates.find((t) => t.id === selected);
                const name = selectedTemplate?.name ?? String(selected);
                return selected === defaultTemplateId ? `${name} *` : name;
              }}
            >
              <MenuItem value={0}>
                <em>Select clinical template</em>
              </MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                  {t.id === defaultTemplateId ? ' *' : ''}
                </MenuItem>
              ))}
              {!isLoadingTemplates && templates.length === 0 && (
                <MenuItem value={0} disabled>
                  <em>No templates available</em>
                </MenuItem>
              )}
            </Select>
          </FormControl>
          {defaultTemplateId !== 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              * Selected based on recent activity in patient's chart
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          onClick={handleCheckCapacity}
          disabled={selectedTemplateId === 0 || isChecking}
        >
          Check Capacity
        </Button>
      </Box>

      {isChecking ? (
        <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={28} />
          <Typography variant="body1" color="text.secondary">
            Checking capacity…
          </Typography>
        </Paper>
      ) : result && overallMeta ? (
        <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
          {/* Overall result header */}
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <overallMeta.Icon sx={{ color: overallMeta.color, fontSize: 40 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: overallMeta.color }}>
                {overallMeta.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {result.templateName} · Last checked {format(new Date(result.checkedAt), "MMM d, yyyy 'at' h:mm a")}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Section 1: Admit visit slots */}
          <Box sx={{ p: 3 }}>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
              Admit Visit Slots ({result.admitSlotsDay === 'tomorrow' ? 'Tomorrow' : 'Today'})
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              {result.admitSlots.length === 0 ? (
                <>
                  <Typography variant="body2" color="error.main">
                    No admit visit slots available {result.admitSlotsDay}.
                  </Typography>
                  {result.admitSlotsDay === 'today' && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCheckTomorrow}
                      disabled={isChecking}
                      sx={{ mt: 1.5 }}
                    >
                      Check tomorrow
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {result.admitSlotsDay === 'tomorrow' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Showing tomorrow's availability.
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {result.admitSlots.map((slot) => (
                      <Chip
                        key={slot}
                        label={slot}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Section 2: Upcoming visits per subsequent day */}
          <Box sx={{ p: 3 }}>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
              Ongoing Capacity
            </Typography>
            {result.days.length === 0 || result.days.every((d) => d.visits.length === 0) ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                This template has no scheduled visits.
              </Typography>
            ) : (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {result.days.map((day) => (
                  <Box key={day.label}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      {day.label}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {day.visits.map((visit, idx) => {
                        const meta = CAPACITY_STATUS_META[visit.status];
                        return (
                          <Box
                            key={`${visit.visitTypeName}-${idx}`}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}
                          >
                            <meta.Icon sx={{ color: meta.color, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, width: 180, flexShrink: 0 }}>
                              {visit.visitTypeName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 150, flexShrink: 0 }}>
                              {visit.timeRange}
                            </Typography>
                            <Typography variant="caption" sx={{ color: meta.color, fontWeight: 600 }}>
                              {CELL_LABEL[visit.status]}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Paper>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No capacity check has been run yet.
        </Typography>
      )}
    </Box>
  );
};

export default CapacityTab;
